import { Head } from "@inertiajs/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	FormPageContainer,
	FormPageHeader,
} from "@/components/admin-portal/shared/page-layout";
import type { FormTherapistProps } from "@/components/admin-portal/therapist/upsert-form";
import type { Location } from "@/types/admin-portal/location";
import type { Service } from "@/types/admin-portal/service";
import type {
	RegistrationNumberSuggestion,
	Therapist,
	TherapistEmploymentStatus,
	TherapistEmploymentType,
	TherapistGender,
	TherapistServiceEnums,
} from "@/types/admin-portal/therapist";
import FormTherapist from "../../../components/admin-portal/therapist/upsert-form";

export type FormMode = "create" | "update";

export interface UpsertTherapistPageProps {
	currentPath: string;
	therapist: Partial<Therapist>;
	genders: TherapistGender;
	employmentTypes: TherapistEmploymentType;
	employmentStatuses: TherapistEmploymentStatus;
	services: Service[];
	locations: Location[];
	registrationNumberSuggestions: RegistrationNumberSuggestion[];
	therapistEnums: TherapistServiceEnums[];
}

/** The edit page's Alamat section props; absent on the create page. */
type AddressPageProps = Partial<
	NonNullable<FormTherapistProps["addressProps"]>
>;

export default function UpsertTherapistPage({
	currentPath,
	therapist,
	genders,
	employmentTypes,
	employmentStatuses,
	services,
	locations,
	registrationNumberSuggestions,
	therapistEnums,
	relocationSupported,
	canManageAddresses,
	timeline,
	correctionLocations,
	upcomingVisits,
}: UpsertTherapistPageProps & AddressPageProps) {
	const { t } = useTranslation("therapists");
	const formMode = useMemo<FormMode>(
		() => (therapist?.id ? "update" : "create"),
		[therapist],
	);
	const formHeader = useMemo(
		() => ({
			title: t(`form.title.${formMode}`),
			description: t(`form.description.${formMode}`),
		}),
		[formMode, t],
	);

	return (
		<>
			{/* required if using Here Map Component */}
			<Head title={formHeader.title}>
				<link
					rel="stylesheet"
					type="text/css"
					href="https://js.api.here.com/v3/3.1/mapsjs-ui.css"
				/>
			</Head>

			<FormPageContainer>
				<section className="flex flex-col justify-center gap-4 mx-auto w-12/12 xl:w-8/12">
					<FormPageHeader
						title={formHeader.title}
						description={formHeader.description}
					/>

					<FormTherapist
						mode={formMode}
						currentPath={currentPath}
						therapist={therapist}
						genders={genders}
						employmentTypes={employmentTypes}
						employmentStatuses={employmentStatuses}
						services={services}
						locations={locations}
						registrationNumberSuggestions={registrationNumberSuggestions}
						therapistEnums={therapistEnums}
						addressProps={
							formMode === "update"
								? {
										relocationSupported: !!relocationSupported,
										canManageAddresses: !!canManageAddresses,
										timeline,
										correctionLocations: correctionLocations ?? [],
										upcomingVisits,
									}
								: undefined
						}
					/>
				</section>
			</FormPageContainer>
		</>
	);
}
